<script setup lang="ts">
// Renders an encrypted attachment. The server only holds it until every device it was for
// has it, 30 days at most (HRTH-110), so what you download stays on this device: images
// and files show a preview until you choose to download them; playing audio or video
// streams it and keeps a copy. Once kept, everything plays and opens from the device.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { MediaRef } from '../api/types'
import { AttachmentGone, formatBytes, keepAttachment, mediaKind, streamUrl } from '../lib/media'
import { localAttachment } from '../lib/attachmentStore'
import { arrived, notTransferred } from '../lib/transfer'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import { useParty } from '../stores/party'
import { usePlayer } from '../stores/player'
import MediaControls from './MediaControls.vue'
import ViewerFrame from './ViewerFrame.vue'

const props = defineProps<{ media: MediaRef; conv?: string }>()
const session = useSession()
const party = useParty()
const player = usePlayer()

async function watchTogether(): Promise<void> {
  if (!props.conv) return
  player.stop()
  await party.start(props.conv, [{ source: 'attachment', media: props.media }]).catch((e) => session.toast(describeError(e), 'error'))
}
const kind = computed(() => mediaKind(props.media.mime))
const el = ref<HTMLMediaElement | null>(null)
const frame = ref<HTMLElement | null>(null)
const controls = ref<InstanceType<typeof MediaControls> | null>(null)
/** What the image or player shows: the kept copy, or a stream from the server. */
const src = ref<string | null>(null)
/** This device has it (downloaded, played, or sent from here). */
const kept = ref(false)
const gone = ref(false)
// Not brought over when this device was linked (HRTH-202), and the server has let it go.
const goneText = computed(() => (notTransferred.has(props.media.id) ? 'Not transferred to this device' : 'No longer on the server'))
const error = ref<string | null>(null)
const busy = ref(false)
/** The image full screen, in the app rather than a new tab (HRTH-188). */
const viewing = ref(false)
let objectUrl: string | null = null

const token = () => session.engine?.token ?? ''
const host = () => (props.conv ? session.conversations.get(props.conv)?.host : undefined) ?? null

function show(blob: Blob): void {
  if (objectUrl) URL.revokeObjectURL(objectUrl)
  objectUrl = URL.createObjectURL(blob)
  src.value = objectUrl
  kept.value = true
}

onMounted(async () => {
  try {
    const local = await localAttachment(props.media.id)
    if (local) return show(local)
    // Audio and video stream until played (then they're kept); images wait for a click.
    if (kind.value === 'audio' || kind.value === 'video') {
      src.value = await streamUrl(props.media, token(), host())
      if (!src.value) await keep()
    }
  } catch (e) {
    error.value = (e as Error).message
  }
})

onBeforeUnmount(() => {
  if (objectUrl) URL.revokeObjectURL(objectUrl)
})

// A history transfer kept it here after this message was already showing (HRTH-202).
watch(
  () => arrived.has(props.media.id),
  async (now) => {
    if (!now || kept.value) return
    const local = await localAttachment(props.media.id)
    if (local) {
      gone.value = false
      error.value = null
      show(local)
    }
  },
)

/** Downloads it to this device (the server may then let its copy go). */
async function keep(): Promise<Blob | null> {
  busy.value = true
  try {
    const blob = await keepAttachment(props.media, token(), host())
    // A playing stream carries on; images and files show the kept copy.
    if (kind.value !== 'audio' && kind.value !== 'video') show(blob)
    else if (!src.value) show(blob)
    else kept.value = true
    return blob
  } catch (e) {
    if (e instanceof AttachmentGone) gone.value = true
    else error.value = (e as Error).message
    return null
  } finally {
    busy.value = false
  }
}

/** Playing is downloading: the whole file is fetched and kept alongside the stream. */
function onPlay(): void {
  if (!kept.value && !busy.value && !gone.value) void keep()
}

/** A stream that fails with no copy here: the server has let it go. */
function onMediaError(): void {
  if (!kept.value) gone.value = true
}

async function save(): Promise<void> {
  const blob = kept.value && objectUrl ? null : await keep()
  if (!objectUrl && !blob) return
  const a = document.createElement('a')
  a.href = objectUrl ?? URL.createObjectURL(blob!)
  a.download = props.media.name
  a.click()
}

const boxStyle = computed(() => {
  const { width, height } = props.media
  if (!width || !height) return {}
  // Fits 300×300 keeping its shape (a tall phone screenshot gets narrower, not squashed), and
  // never wider than the thread.
  const w = Math.round(Math.min(width, 300, (300 * width) / height))
  return { width: `${w}px`, maxWidth: '100%', aspectRatio: `${width} / ${height}` }
})
</script>

<template>
  <div class="mt-1" :data-testid="`media-${kind}`" :data-name="media.name">
    <button
      v-if="(kind === 'video' || kind === 'audio') && conv"
      class="mono mb-1.5 text-text-muted uppercase hover:text-text-primary"
      data-testid="attachment-watch-together"
      @click="watchTogether"
    >
      {{ kind === 'video' ? '▶ Watch together' : '∿ Listen together' }}
    </button>
    <template v-if="kind === 'image'">
      <button v-if="src" class="block max-w-full cursor-zoom-in" :aria-label="`View ${media.name}`" data-testid="media-image-open" @click="viewing = true">
        <img :src="src" :alt="media.name" class="max-h-[300px] max-w-full border border-border object-contain" :style="boxStyle" data-testid="media-image-loaded" />
      </button>
      <!-- Not on this device yet: a blurred preview until it's downloaded. -->
      <div v-else class="relative max-w-full overflow-hidden border border-border" :style="boxStyle" data-testid="media-image-preview">
        <img v-if="media.thumb" :src="media.thumb" alt="" class="h-full w-full scale-110 object-cover blur-md" :style="boxStyle" />
        <div v-else class="field-grid h-40 w-60" />
        <div class="absolute inset-0 flex items-center justify-center bg-black/30">
          <span v-if="gone" class="mono bg-bg-primary px-2 py-1 text-text-muted" data-testid="media-gone">{{ goneText }}</span>
          <button v-else class="btn-primary" :disabled="busy" data-testid="media-download" @click="keep">
            {{ busy ? '…' : `Download · ${formatBytes(media.size)}` }}
          </button>
        </div>
      </div>
      <ViewerFrame v-if="viewing && src" :title="media.name" @close="viewing = false">
        <template #sub>{{ formatBytes(media.size) }}</template>
        <template #actions>
          <button class="mono px-3 py-2 text-white/80 uppercase hover:text-white" data-testid="media-viewer-save" @click="save">Save</button>
        </template>
        <div class="flex min-h-0 flex-1 items-center justify-center">
          <img :src="src" :alt="media.name" class="max-h-full max-w-full object-contain" data-testid="media-viewer-image" />
        </div>
      </ViewerFrame>
    </template>

    <div v-else-if="kind === 'audio' && src && !(gone && !kept)" class="max-w-sm border border-border px-1">
      <audio ref="el" :src="src" preload="metadata" data-testid="media-player" @play="onPlay" @error="onMediaError" />
      <MediaControls :el="el" kind="audio" />
    </div>

    <div
      v-else-if="kind === 'video' && src && !(gone && !kept)"
      ref="frame"
      class="media-frame inline-block max-w-full min-w-64 border border-border bg-black text-white"
    >
      <video
        ref="el"
        :src="src"
        preload="metadata"
        playsinline
        class="block max-h-96 max-w-full cursor-pointer"
        data-testid="media-player"
        @click="controls?.toggle()"
        @dblclick="controls?.toggleFullscreen()"
        @play="onPlay"
        @error="onMediaError"
      />
      <MediaControls ref="controls" :el="el" kind="video" :frame="frame" class="px-1" />
    </div>

    <div
      v-if="kind === 'file' || error || ((kind === 'audio' || kind === 'video') && (!src || (gone && !kept)))"
      class="flex max-w-sm items-center gap-3 border border-border px-3 py-2"
    >
      <span class="mono flex h-9 w-9 shrink-0 items-center justify-center border border-border text-text-muted" aria-hidden="true">{{ (media.name.split('.').pop() ?? '').slice(0, 4).toUpperCase() || 'BIN' }}</span>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm text-text-secondary">{{ media.name }}</div>
        <div class="mono text-text-muted" :data-testid="gone && !kept ? 'media-gone' : undefined">
          {{ gone && !kept ? goneText : (error ?? `${formatBytes(media.size)}${kept ? ' · on this device' : ''}`) }}
        </div>
      </div>
      <button v-if="!(gone && !kept)" class="btn-ghost" :disabled="busy" data-testid="media-download" @click="save">
        {{ busy ? '…' : kept ? 'Save' : 'Download' }}
      </button>
    </div>
  </div>
</template>
